import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Input } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { CanvasAPI } from '@/api/canvas'
import { AssetDomain, AssetTransferStatus } from '@/api/type-response'
import { Button, Loading, message, Modal, Trans } from '@/components'
import { getBonusStat } from '@/models/credit'
import { useUserStore } from '@/stores/user'
import { formatDecimalString, formatPrice } from '@/utils/formatter'
import { findModelRouteTransferTarget } from '@/utils/modelRouteTransferTarget'
import { cn } from '@/utils/style'

const PRESET_CREDITS = [100, 50, 30, 10]
const TRANSFER_TARGET_KEYWORD = import.meta.env.VITE_MODEL_ROUTE_TRANSFER_TARGET || ''

/** API takes whole cents; string parsing avoids float rounding (1.1 * 100). */
function creditsToCents(value: string): number | null {
  const match = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(value.trim())
  if (!match) return null
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0') || '0')
}

function RadioDot({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'inline-block w-4 h-4 rounded-full border flex-shrink-0 bg-panel-white',
        checked ? 'border-[5px] border-highlight' : 'border-gray300',
      )}
    />
  )
}

export function ModelRouteRechargeModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('modelRoute')
  const userStore = useUserStore()
  const queryClient = useQueryClient()

  const [presetCredits, setPresetCredits] = useState<number | null>(PRESET_CREDITS[0])
  const [customValue, setCustomValue] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    void userStore.getAccount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: canvasBilling, isLoading: canvasBalanceLoading } = useQuery({
    queryKey: ['modelRouteBillingSummary'],
    queryFn: () => CanvasAPI.getProjectBillingSummary(),
  })

  // Each environment identifies its transfer target by alias or hostname.
  const { data: domainsRes, isLoading: domainsLoading } = useQuery({
    queryKey: ['assetDomains'],
    queryFn: () => API.getAssetDomains(),
  })
  const allDomains = useMemo<AssetDomain[]>(
    () => (domainsRes?.code === 0 ? (domainsRes.assetDomains ?? []) : []),
    [domainsRes],
  )
  const domains = useMemo(() => allDomains.filter((d) => !!d.alias), [allDomains])
  const target = useMemo(
    () => findModelRouteTransferTarget(domains, TRANSFER_TARGET_KEYWORD),
    [domains],
  )

  const targetError = useMemo(() => {
    if (domainsLoading || !domainsRes) return null
    if (domainsRes.code !== 0) return 'loadFailed' as const
    if (allDomains.length > 0 && domains.length === 0) return 'misconfigured' as const
    if (!target) return 'none' as const
    return null
  }, [domainsLoading, domainsRes, allDomains, domains, target])

  // Transferable = recharged credits only (doc §4.1); never totalBonus.
  const bonusStat = getBonusStat(userStore.account)
  const balanceCent = bonusStat ? bonusStat.rechargeBonus.moneyCent : userStore.account.balance

  const amountCent = useMemo(() => {
    if (presetCredits !== null) return presetCredits * 100
    if (!customValue) return null
    return creditsToCents(customValue)
  }, [presetCredits, customValue])

  const customInvalid = presetCredits === null && !!customValue && amountCent === null
  const insufficient = amountCent !== null && amountCent > balanceCent

  const modelRouteBalance = canvasBilling?.summary?.balance_credits
  const modelRouteBalanceStr =
    modelRouteBalance !== undefined ? formatDecimalString(modelRouteBalance, 3, 3) : '-'

  const canSubmit =
    !submitting && !!target && amountCent !== null && amountCent > 0 && !insufficient

  const submit = async () => {
    if (!canSubmit || !target || amountCent === null) return
    setSubmitting(true)
    try {
      const res = await API.createAssetTransfer({
        target_service_host_id: target.serviceHostID,
        amount_cent: amountCent,
      })
      if (res.code !== 0) {
        // business errors are already toasted by the http client
        return
      }

      // The list can still report Frozen right after creation; poll briefly.
      let status: AssetTransferStatus | null = null
      const transferID = res.transfer?.transferID
      if (transferID) {
        for (let attempt = 0; attempt < 5; attempt++) {
          try {
            const list = await API.getAssetTransfers({ page: 1, per_page: 20 })
            if (list.code === 0) {
              const record = list.transfers?.find((item) => item.id === transferID)
              status = record?.status ?? null
              if (
                status === AssetTransferStatus.Finished ||
                status === AssetTransferStatus.Cancelled
              ) {
                break
              }
            }
          } catch {
            // best-effort
          }
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }

      if (status === AssetTransferStatus.Cancelled) {
        message.error(t('recharge.cancelled', 'Transfer was cancelled and refunded'))
      } else {
        message.success(
          status === AssetTransferStatus.Finished || status === null
            ? t('recharge.success', 'Recharge successful')
            : t('recharge.processing', 'Transfer created and is being processed'),
        )
      }
      void userStore.getAccount()
      void queryClient.invalidateQueries({ queryKey: ['modelRouteBillingSummary'] })
      void queryClient.invalidateQueries({ queryKey: ['modelRouteKpi'] })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      className="w-[640px]"
      open={true}
      onClose={() => {
        if (!submitting) onClose()
      }}
    >
      <Modal.Header>
        <Trans ns="modelRoute" i18nKey="recharge.title">
          Recharge
        </Trans>
      </Modal.Header>

      <Modal.Body className="max-h-none">
        <div className="rounded-2xl py-8 px-6 text-center bg-gradient-to-r from-teal-100/60 via-sky-100/60 to-indigo-200/70">
          <div className="t-sb text-primary mb-3">
            <Trans ns="modelRoute" i18nKey="recharge.balanceTitle">
              Model Route Balance
            </Trans>
          </div>
          <div className="flex items-baseline justify-center gap-2">
            {canvasBalanceLoading ? (
              <Loading className="h-8 w-8" />
            ) : (
              <>
                <span className="h3 text-primary">{modelRouteBalanceStr}</span>
                <span className="t-sb text-primary">
                  <Trans ns="modelRoute" i18nKey="recharge.credits">
                    Credits
                  </Trans>
                </span>
              </>
            )}
          </div>
        </div>

        <div className="t-sb text-with-star mt-6 mb-2">
          <Trans ns="modelRoute" i18nKey="recharge.amountLabel">
            Recharge Credits
          </Trans>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {PRESET_CREDITS.map((value) => (
            <button
              key={value}
              type="button"
              disabled={submitting}
              className={cn(
                'flex items-center gap-3 px-4 py-3 h-14 border rounded-lg text-left',
                presetCredits === value
                  ? 'border-secondary bg-highlight/10'
                  : 'border-gray100 hover:border-secondary',
              )}
              onClick={() => {
                setPresetCredits(value)
                setCustomValue('')
              }}
            >
              <RadioDot checked={presetCredits === value} />
              <span className="t-sb text-primary">
                {value}{' '}
                <Trans ns="modelRoute" i18nKey="recharge.credits">
                  Credits
                </Trans>
              </span>
            </button>
          ))}
          <div className="col-span-2">
            <Input
              disabled={submitting}
              suffix={
                <span className="t-sb text-primary">
                  <Trans ns="modelRoute" i18nKey="recharge.credits">
                    Credits
                  </Trans>
                </span>
              }
              inputMode="decimal"
              maxLength={10}
              placeholder={t('recharge.customPlaceholder', 'Custom Quantity')}
              className="w-full items-center t-sb px-4 h-14 border border-gray100 rounded-lg hover:border-secondary"
              value={customValue}
              onFocus={() => setPresetCredits(null)}
              onChange={(e) => {
                const sanitized = e.target.value
                  .replace(/[^\d.]/g, '')
                  .replace(/^(\d*\.\d{0,2}).*$/, '$1')
                  .replace(/^(\d*)\..*?\./, '$1.')
                setPresetCredits(null)
                setCustomValue(sanitized)
              }}
            />
          </div>
        </div>
        {customInvalid && (
          <div className="mt-2 t-sm text-error">
            <Trans ns="modelRoute" i18nKey="recharge.invalidAmount">
              Enter a valid amount (up to 2 decimal places)
            </Trans>
          </div>
        )}
        {insufficient && (
          <div className="mt-2 t-sm text-error">
            <Trans ns="modelRoute" i18nKey="recharge.insufficientBalance">
              Insufficient transferable balance
            </Trans>
          </div>
        )}

        <div className="t-sb text-with-star mt-6 mb-2">
          <Trans ns="modelRoute" i18nKey="recharge.paymentMethod">
            Payment Method
          </Trans>
        </div>
        <div className="border border-gray100 rounded-lg px-4 py-3 flex items-center gap-3">
          <RadioDot checked />
          <span className="t-bold text-primary">
            <Trans ns="modelRoute" i18nKey="recharge.transferableBalance">
              Glows Transferable Balance
            </Trans>
          </span>
          <span className="t-sm px-4 py-1 rounded-full bg-neutral-50 flex gap-2">
            <span className="text-highlight">{formatPrice(balanceCent ?? 0, false, '')}</span>
            <span className="text-secondary">
              <Trans ns="modelRoute" i18nKey="recharge.credits">
                Credits
              </Trans>
            </span>
          </span>
        </div>
        <div className="mt-2 t-sm text-secondary">
          <Trans ns="modelRoute" i18nKey="recharge.transferableHint">
            Only recharged credits can be transferred. Gift and bonus credits are excluded, so this
            may be lower than the balance shown in the header.
          </Trans>
        </div>
        {targetError === 'loadFailed' && (
          <div className="mt-3 t-sm text-error">
            <Trans
              ns="modelRoute"
              i18nKey="recharge.targetLoadFailed"
              values={{ code: domainsRes?.code ?? '-' }}
            >
              Failed to load transfer targets (code {'{{code}}'}). Please try again later.
            </Trans>
          </div>
        )}
        {targetError === 'none' && (
          <div className="mt-3 t-sm text-error">
            <Trans ns="modelRoute" i18nKey="recharge.noTarget">
              No transfer target is available yet. Please contact support.
            </Trans>
          </div>
        )}
        {targetError === 'misconfigured' && (
          <div className="mt-3 t-sm text-error">
            <Trans ns="modelRoute" i18nKey="recharge.targetMisconfigured">
              Transfer target is misconfigured (missing alias). Please contact support.
            </Trans>
          </div>
        )}

        <div className="flex items-center justify-end gap-4 mt-8">
          <Button
            type="outline"
            size="md"
            className="min-w-[88px]"
            onClick={() => {
              if (!submitting) onClose()
            }}
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type="secondary"
            size="md"
            className="min-w-[112px]"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            <Trans ns="modelRoute" i18nKey="recharge.confirm">
              Recharge
            </Trans>
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  )
}
